/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0; you may not use this file except in compliance with the Elastic License
 * 2.0.
 */

// DO NOT MODIFY THIS FILE BY HAND. IT IS GENERATED BY
// `yarn workspace @elastic/esql-definitions generate` FROM
// `elasticsearch/esql/x-pack-esql/definition/operators/greater_than_or_equal.json`.

import type { OperatorDefinition } from '../../definition_types';

/**
 * Check if one field is greater than or equal to another. If either field is multivalued then the result is `null`.
 *
 * Note: This is pushed to the underlying search index if one side of the comparison is constant and the other side is a field in the index that has both an mapping-index and doc-values.
 */
const definition: OperatorDefinition = {
  operator: '>=',
  name: 'greater_than_or_equal',
  signatures: [
    {
      params: [
        {
          name: 'lhs',
          type: 'date',
          optional: false,
        },
        {
          name: 'rhs',
          type: 'date',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'lhs',
          type: 'date',
          optional: false,
        },
        {
          name: 'rhs',
          type: 'date_nanos',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'lhs',
          type: 'date_nanos',
          optional: false,
        },
        {
          name: 'rhs',
          type: 'date',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'lhs',
          type: 'date_nanos',
          optional: false,
        },
        {
          name: 'rhs',
          type: 'date_nanos',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'lhs',
          type: 'double',
          optional: false,
        },
        {
          name: 'rhs',
          type: 'double',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'lhs',
          type: 'double',
          optional: false,
        },
        {
          name: 'rhs',
          type: 'integer',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'lhs',
          type: 'double',
          optional: false,
        },
        {
          name: 'rhs',
          type: 'long',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'lhs',
          type: 'integer',
          optional: false,
        },
        {
          name: 'rhs',
          type: 'double',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'lhs',
          type: 'integer',
          optional: false,
        },
        {
          name: 'rhs',
          type: 'integer',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'lhs',
          type: 'integer',
          optional: false,
        },
        {
          name: 'rhs',
          type: 'long',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'lhs',
          type: 'ip',
          optional: false,
        },
        {
          name: 'rhs',
          type: 'ip',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'lhs',
          type: 'keyword',
          optional: false,
        },
        {
          name: 'rhs',
          type: 'keyword',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'lhs',
          type: 'keyword',
          optional: false,
        },
        {
          name: 'rhs',
          type: 'text',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'lhs',
          type: 'long',
          optional: false,
        },
        {
          name: 'rhs',
          type: 'double',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'lhs',
          type: 'long',
          optional: false,
        },
        {
          name: 'rhs',
          type: 'integer',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'lhs',
          type: 'long',
          optional: false,
        },
        {
          name: 'rhs',
          type: 'long',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'lhs',
          type: 'text',
          optional: false,
        },
        {
          name: 'rhs',
          type: 'keyword',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'lhs',
          type: 'text',
          optional: false,
        },
        {
          name: 'rhs',
          type: 'text',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'lhs',
          type: 'unsigned_long',
          optional: false,
        },
        {
          name: 'rhs',
          type: 'unsigned_long',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'lhs',
          type: 'version',
          optional: false,
        },
        {
          name: 'rhs',
          type: 'version',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
  ],
  preview: false,
  snapshotOnly: false,
};

export default definition;
